import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from "react-toastify";
import * as actions from '../../../redux/actions/growthEngine';
import {
  useArtistTrackList,
  useSearchArtistTracks,
  useAlbumPlaylistTracks,
} from '../../../queries/artistTracks.queries';
import SearchIcon from "../../../assets/images/icons/search.svg";
import SpotifyIcon from "../../../assets/images/icons/spotify-icon.png";

const tabs = [
  { name: 'Single', value: 'track' },
  { name: 'Project', value: 'album' },
  { name: 'Playlist', value: 'playlist' },
  { name: 'Artist', value: 'artist' },
];

const useDebouncedValue = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

export const FeatureFM = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const {
    artistTrackType,
    selectedRelease,
    selectedAlbumPlaylistTrack,
    artistSpotifyProfileUrl,
    formData,
  } = useSelector(state => state.growthEngine);
  const { artistInfo } = useSelector(state => state.artistDetails);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 500);
  const detailRef = useRef(null);

  const isListTab = ['track', 'album', 'playlist'].includes(artistTrackType);
  const isSearchMode = !!searchQuery.trim();
  const shouldFetchList = isListTab && !isSearchMode;

  const {
    data: trackList = [],
    isLoading: isTrackListLoading,
    isFetching: isTrackListFetching,
  } = useArtistTrackList(artistTrackType, shouldFetchList);

  const {
    data: searchResult,
    isLoading: isSearchLoading,
    isFetching: isSearchFetching,
  } = useSearchArtistTracks(
    {
      artistTrackType,
      query: debouncedSearch,
      artistId: artistInfo?.spotifyArtistId,
      artistName: artistInfo?.spotifyArtistName,
    },
    isListTab && !!debouncedSearch.trim()
  );

  const artistTrackList = isSearchMode
    ? (searchResult?.list || [])
    : trackList;

  const isListLoading = isTrackListLoading || isSearchLoading;
  const isListRefreshing = isTrackListFetching || isSearchFetching;

  const {
    data: albumPlaylistTracks = { items: [] },
    isLoading: isAlbumPlaylistTracksLoading,
  } = useAlbumPlaylistTracks(
    {
      id: selectedRelease?.id,
      type: selectedRelease?.type,
    },
    selectedRelease?.type === 'album' || selectedRelease?.type === 'playlist'
  );

  useEffect(() => {
    if (searchResult?.type && searchResult.type !== artistTrackType) {
      dispatch(actions.updateTrackType(searchResult.type));
    }
  }, [searchResult?.type, artistTrackType, dispatch]);

  useEffect(() => {
    const hasSelectedRelease = selectedRelease && Object.keys(selectedRelease).length > 0;

    if (hasSelectedRelease) {
      if (detailRef.current) {
        detailRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      if (['track', 'album', 'playlist'].includes(selectedRelease?.type)) {
        dispatch(actions.updateTrackType(selectedRelease.type));
      }
      return;
    }

    if (!artistSpotifyProfileUrl) {
      dispatch(actions.updateTrackType(
        actions.getDefaultTrackTypeForObjective(formData?.objective)
      ));
    }
  }, [dispatch, selectedRelease, artistSpotifyProfileUrl, formData?.objective]);

  const handleRefresh = () => {
    setSearchQuery('');
    dispatch(actions.updateTrackType(
      actions.getDefaultTrackTypeForObjective(formData?.objective)
    ));
    queryClient.invalidateQueries({ queryKey: ['artistTracks'] });
    queryClient.invalidateQueries({ queryKey: ['artistTrackSearch'] });
    queryClient.invalidateQueries({ queryKey: ['albumPlaylistTracks'] });
  };

  const MusicLoader = () => (
    <div className="flex justify-center items-center h-32">
      <div className="flex space-x-2">
        <div className="w-2 h-6 bg-theme_clr2 animate-pulse" style={{ animationDelay: '0s' }}></div>
        <div className="w-2 h-6 bg-theme_clr2 animate-pulse" style={{ animationDelay: '0.1s' }}></div>
        <div className="w-2 h-6 bg-theme_clr2 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
        <div className="w-2 h-6 bg-theme_clr2 animate-pulse" style={{ animationDelay: '0.3s' }}></div>
      </div>
    </div>
  );

  const handleSelectRelease = (value) => {
    dispatch(actions.selectRelease(value));
    if (value.type === 'album' || value.type === 'playlist') {
      dispatch(actions.selectAlbumPlaylistTrack({}));
    }
    dispatch(actions.addArtistURL(''));
  };

  const selectAlbumPlaylistTracks = (value) => {
    dispatch(actions.selectAlbumPlaylistTrack(value));
  };

  const handleTabClick = (tabValue) => {
    setSearchQuery('');
    dispatch(actions.updateTrackType(tabValue));
  };

  return (
    <div className="FeatureFM-page bg-theme_clr1 font-inter py-5" data-tour="choose-release">
      <div className="container max-w-[875px]">
        <section>
          <div className="heading text-white font-medium text-center mb-[28.5px]">
            <h5 className="text-[22.5px] mb-2">Choose your Release</h5>
            <div className="description text-white text-opacity-80 text-[15px]">Choose the song, project or playlist you want to promote.</div>
          </div>

          <div className="info-box bg-white rounded-xl">
            <div className="wrapper border-b p-4 lg:p-5">
              <div className="text-[18px] font-bold mb-4">Select the release you want to promote.</div>
              <div className="text-base font-medium flex flex-wrap justify-between gap-2">
                <div className="text-black">Recent Release</div>
                <div className="text-theme_clr2 cursor-pointer" onClick={handleRefresh}>
                  <i className={`bi bi-arrow-clockwise ${isListRefreshing ? 'animate-spin' : ''}`}></i>
                  {isListRefreshing ? ' Refreshing…' : ' Refresh'}
                </div>
              </div>
            </div>
            <div className="content-wrapper p-4 lg:p-5">
              <div className="search-box relative mb-4">
                <span className="icon absolute start-[16px] top-1/2 transform -translate-y-1/2 z-1">
                  <img src={SearchIcon} alt="Search Icon" className="block opacity-60" />
                </span>
                <input
                  type="search"
                  className="h-[45px] w-full bg-white border border-slate-300 text-base text-black placeholder:text-gray-400 focus:outline-none rounded-md ps-[45px] pe-4 py-2"
                  placeholder="Search for your song, album, or playlist"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="tabs-wrapper">
                <div className="flex border-b-2 border-slate-300 mb-4">
                  {tabs.map((tab) => (
                    <button
                      key={tab.name}
                      type="button"
                      onClick={() => handleTabClick(tab.value)}
                      className={`font-semibold border-b-2 border-slate-300 mb-[-2px] px-4 py-2 ${artistTrackType === tab.value ? 'border-theme_clr2 text-theme_clr2' : 'text-gray-500 hover:text-blue-500'}`}
                    >
                      {tab.name}
                    </button>
                  ))}
                </div>

                {artistTrackType === 'track' && (
                  (isListLoading || isListRefreshing) ? (
                    <MusicLoader />
                  ) : artistTrackList?.length > 0 ? (
                    <div className="max-h-[500px] overflow-auto space-y-3">
                      {artistTrackList.map((track) => (
                        <div
                          key={track.id}
                          className={`flex items-center bg-white border rounded shadow p-4 space-x-3 md:space-x-4 cursor-pointer ${selectedRelease?.id === track.id ? 'border-blue-500 ring-2 ring-blue-400' : ''}`}
                          onClick={() => handleSelectRelease({ ...track, type: 'track' })}
                        >
                          <img src={track?.album?.images[0]?.url} alt={track?.name} className="w-12 h-12 rounded-xl object-cover" />
                          <div className="info truncate">
                            <div className="text-[18px] font-medium truncate">{track?.name}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-4">No tracks found.</div>
                  )
                )}

                {artistTrackType === 'album' && (
                  (isListLoading || isListRefreshing) ? (
                    <MusicLoader />
                  ) : artistTrackList?.length > 0 ? (
                    <div className="max-h-[500px] overflow-auto space-y-3">
                      {artistTrackList.map((album) => (
                        <div
                          key={album.id}
                          className={`flex items-center bg-white border rounded shadow p-4 space-x-3 md:space-x-4 cursor-pointer ${selectedRelease?.id === album.id ? 'border-blue-500 ring-2 ring-blue-400' : ''}`}
                          onClick={() => handleSelectRelease({ ...album, type: 'album' })}
                        >
                          <img src={album.images[0].url} alt={album.name} className="w-12 h-12 rounded-xl object-cover" />
                          <div className="info truncate">
                            <div className="text-[18px] font-medium truncate">{album.name}</div>
                            {album.total_tracks && (
                              <div className="flex items-center gap-2 text-gray-500 text-sm">
                                Total tracks
                                <span className="h-[5px] w-[5px] inline-block bg-gray-400 rounded-full"></span>
                                <span>{album.total_tracks}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-4">No albums found.</div>
                  )
                )}

                {artistTrackType === 'playlist' && (
                  (isListLoading || isListRefreshing) ? (
                    <MusicLoader />
                  ) : artistTrackList?.filter(p => p !== null)?.length > 0 ? (
                    <div className="max-h-[500px] overflow-auto space-y-3">
                      {artistTrackList.filter(p => p !== null).map((playlist) => (
                        <div
                          key={playlist.id}
                          className={`flex items-center bg-white border rounded shadow p-4 space-x-3 md:space-x-4 cursor-pointer ${selectedRelease?.id === playlist.id ? 'border-blue-500 ring-2 ring-blue-400' : ''}`}
                          onClick={() => handleSelectRelease({ ...playlist, type: 'playlist' })}
                        >
                          <img src={playlist?.images[0].url} alt={playlist?.name} className="w-12 h-12 rounded-xl object-cover" />
                          <div className="info truncate">
                            <div className="text-[18px] font-medium truncate">{playlist?.name}</div>
                            {playlist?.tracks?.total && (
                              <div className="flex items-center gap-2 text-gray-500 text-sm">
                                Total tracks
                                <span className="h-[5px] w-[5px] inline-block bg-gray-400 rounded-full"></span>
                                <span>{playlist?.tracks?.total}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-4">No playlists found.</div>
                  )
                )}

                {artistTrackType === "artist" && (
                  <div className="max-h-[500px] overflow-auto space-y-3">
                    <div
                      className={`flex items-center bg-white border rounded shadow p-4 space-x-3 md:space-x-4 cursor-pointer ${artistSpotifyProfileUrl ? "border-blue-500 ring-2 ring-blue-400" : ""}`}
                      onClick={() => {
                        const spotifyUrl = artistInfo?.links?.find((item) => item.source === "spotify")?.url || "";
                        if (!spotifyUrl) {
                          toast.warn("You have not artist profile");
                          return;
                        }
                        dispatch(actions.addArtistURL(spotifyUrl));
                        dispatch(actions.updateFormData({ destinationUrl: spotifyUrl }));
                        dispatch(actions.selectRelease({}));
                        dispatch(actions.selectAlbumPlaylistTrack({}));
                      }}
                    >
                      <div className="relative w-12 h-12">
                        <img src={artistInfo?.avatar || SpotifyIcon} alt={artistInfo?.name} className="w-12 h-12 rounded-xl object-cover" />
                        <img src={SpotifyIcon} alt="Spotify" className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full p-[2px]" />
                      </div>
                      <div className="info truncate">
                        <div className="text-[18px] font-medium truncate">{artistInfo?.name}</div>
                        <div className="text-gray-500 text-sm truncate">
                          {artistInfo?.links?.find((item) => item.source === "spotify")?.url || "No Spotify Profile"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {selectedRelease && Object.keys(selectedRelease).length > 0 && (
          <section ref={detailRef} className="mt-10">
            <div className="heading text-white font-medium text-center mb-[28.5px]">
              <h5 className="text-[22.5px] mb-2">Choose your Release</h5>
              <div className="description text-white text-opacity-80 text-[15px]">
                Choose the song, project or playlist you want to promote. Choose a song to autoplay when your ad is clicked.
              </div>
            </div>

            <div className="info-box bg-white rounded-xl">
              <div className="content-wrapper divide-y divide-gray-100">
                <div className="flex items-center space-x-4 p-4">
                  <img
                    src={
                      selectedRelease?.images?.[0]?.url ||
                      selectedRelease?.album?.images?.[0]?.url ||
                      'https://via.placeholder.com/48'
                    }
                    alt=""
                    className="w-12 h-12 rounded-xl object-cover"
                  />
                  <div className="text-black text-base font-bold">{selectedRelease?.name}</div>
                </div>

                <div className="creator grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-3 p-4">
                  <div className="label-name text-black text-base font-bold">Creator</div>
                  <div className="label-value text-gray-700 uppercase">
                    {(selectedRelease?.owner?.display_name || selectedRelease?.artists?.[0]?.name || 'Unknown').toUpperCase()}
                  </div>
                </div>

                <div className="type grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-3 p-4">
                  <div className="label-name text-black text-base font-bold">Type</div>
                  <div className="label-value text-gray-700 uppercase">{selectedRelease?.type || 'N/A'}</div>
                </div>

                <div className="found grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-3 p-4">
                  <div className="label-name text-black text-base font-bold">Found On</div>
                  <div className="label-value text-gray-700 uppercase">
                    Spotify <i className="bi bi-spotify"></i>
                  </div>
                </div>

                {(selectedRelease?.type === 'playlist' || selectedRelease?.type === 'album') && (
                  <div className="p-4">
                    <div className="text-base text-black font-bold mb-1">Autoplay a Track</div>
                    <div className="text-gray-500 text-sm mb-3">
                      You can select a track from your playlist to autoplay when someone clicks on your ad.
                    </div>
                    {isAlbumPlaylistTracksLoading ? (
                      <MusicLoader />
                    ) : albumPlaylistTracks?.items?.length > 0 ? (
                      <select
                        className="h-[45px] w-full bg-white border border-slate-300 text-base text-black focus:outline-none rounded-md px-4 py-2"
                        value={selectedAlbumPlaylistTrack?.id || '0'}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedObj = albumPlaylistTracks?.items.find(item => item.id === selectedId);
                          selectAlbumPlaylistTracks(selectedObj);
                        }}
                      >
                        <option value="0">Select a song</option>
                        {albumPlaylistTracks.items.map((item) => (
                          <option key={item.id} value={item.id}>{item?.name}</option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-center text-gray-500 py-2">No tracks found for this release.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
